import React, { useState } from 'react';
import { Table, Tag, Input, DatePicker, Button, Space } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import moment from 'moment';

const HistoryTasksTable = ({ tasks, materials, loading = false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const pageSize = 20;
  
  
  const getMaterialName = (materialId) => {
    if (!materialId || materialId === "-") return "-";
    const material = materials.find(m => m.id === materialId);
    return material ? material.name : materialId; 
  };

  
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleString('en-US');
    } catch (error) {
      console.error('Error formatting date:', error);
      return "Error";
    }
  };

  
  const getFilteredTasks = () => {
    
    const validTasks = Array.isArray(tasks) ? tasks : [];
    
    let filteredTasks = [...validTasks];
    
    
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredTasks = filteredTasks.filter(task => 
        (task.jobNumber && task.jobNumber.toLowerCase().includes(searchLower)) || 
        (task.customer && task.customer.toLowerCase().includes(searchLower))
      );
    }
    
    
    if (dateFilter) {
      const filterDateStr = dateFilter.format('YYYY-MM-DD');
      filteredTasks = filteredTasks.filter(task => {
        
        const completedAt = task.completedAt || task.deletedAt || task.timestamp;
        if (!completedAt) return false;
        
        try {
          const taskDate = moment(completedAt);
          return taskDate.isValid() && taskDate.format('YYYY-MM-DD') === filterDateStr;
        } catch (error) {
          return false;
        }
      });
    }
    
    return filteredTasks;
  };

  
  const clearFilters = () => {
    setSearchText('');
    setDateFilter(null);
  };

  
  const columns = [
    {
      title: 'Job Number',
      dataIndex: 'jobNumber',
      key: 'jobNumber',
      render: text => text || "-"
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: text => text || "-"
    },
    {
      title: 'Material',
      dataIndex: 'material',
      key: 'material',
      render: materialId => getMaterialName(materialId)
    },
    {
      title: 'Material Quantity',
      dataIndex: 'materialQuantity',
      key: 'materialQuantity',
      render: text => text || "-"
    },
    {
      title: 'Machine',
      dataIndex: 'machine',
      key: 'machine',
      render: text => text || "-"
    },
    {
      title: 'Creation Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: timestamp => formatDate(timestamp),
    },
    {
      title: 'Completion Time',
      key: 'completionTime',
      render: (_, record) => formatDate(record.completedAt || record.deletedAt),
    },
    {
      title: 'Processing Method',
      dataIndex: 'processingMethod',
      key: 'processingMethod',
      render: method => {
        if (!method) return <Tag color="default">Unknown</Tag>;
        const color = method === 'Completed' ? 'green' : 'gray';
        return <Tag color={color}>{method}</Tag>;
      },
    },
  ];

  
  const getRowClassName = (record) => {
    return record.processingMethod === 'Completed' ? 'completed' : 'deleted';
  };

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Input
            placeholder="Search job number or customer"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            disabled={loading}
          />
          <DatePicker 
            placeholder="Filter by completion date" 
            value={dateFilter}
            onChange={setDateFilter}
            disabled={loading}
          />
          <Button 
            icon={<ClearOutlined />} 
            onClick={clearFilters}
            disabled={loading}
          >
            Clear Filters
          </Button>
        </Space>
        <span>
          Total Records: {getFilteredTasks().length}
        </span>
      </div>

      <Table
        dataSource={getFilteredTasks()}
        columns={columns}
        rowKey={record => record.id ? String(record.id) : `history-${Math.random()}`}
        pagination={{
          pageSize,
          current: currentPage,
          onChange: page => setCurrentPage(page),
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        rowClassName={getRowClassName}
        locale={{ emptyText: 'No history records found' }}
        loading={loading}
      />
    </div>
  );
};

export default HistoryTasksTable;